<script lang="ts">
  import { onMount, onDestroy, setContext } from 'svelte'
  import { page, router } from '@inertiajs/svelte'
  import type { Snippet } from 'svelte'
  import SiteHeader from '~/components/site/SiteHeader.svelte'
  import SiteFooter from '~/components/site/SiteFooter.svelte'
  import SiteFlash from '~/components/site/SiteFlash.svelte'
  import SiteCartDrawer from '~/components/site/SiteCartDrawer.svelte'
  import SiteProductModal from '~/components/site/SiteProductModal.svelte'
  import SiteFloatingCart from '~/components/site/SiteFloatingCart.svelte'
  import { SiteUiState, SITE_UI_KEY } from '~/lib/site-ui-context.svelte'
  import { SITE_CART_KEY, siteCartStore, syncSiteCartFromServer } from '~/lib/site-cart-store.svelte'
  import { readServerSiteCart, resolveSiteCart } from '~/lib/site-cart-view'
  import { fetchSiteCartSnapshot } from '~/lib/site-cart'

  const siteUi = new SiteUiState()

  setContext(SITE_UI_KEY, siteUi)
  setContext(SITE_CART_KEY, siteCartStore)

  interface Props {
    title?: string
    description?: string
    children: Snippet
  }

  let { title = 'Суперфуд Фарм', description, children }: Props = $props()

  function syncFromPageProps() {
    syncSiteCartFromServer(readServerSiteCart($page.props as Record<string, unknown>))
  }

  $effect(() => {
    $page.url
    $page.props
    syncFromPageProps()
  })

  const siteCustomer = $derived(
    ($page.props as { siteCustomer?: { userType: string; approvalStatus: string } | null })
      .siteCustomer ?? null
  )

  /** Корзина и модалка товара — только для одобренных ЮЛ; ФЛ работают через CSA */
  const showSiteCommerce = $derived.by(() => {
    if (!siteCustomer) return true
    if (siteCustomer.approvalStatus !== 'approved') return false
    return siteCustomer.userType === 'legal_entity'
  })

  const cartHasItems = $derived(
    showSiteCommerce &&
      resolveSiteCart(siteCartStore.snapshot, $page.props as Record<string, unknown>).count > 0
  )

  onMount(() => {
    document.documentElement.classList.remove('dark')
    document.documentElement.style.backgroundColor = '#09100a'
    document.body.classList.add('site-ui')

    syncFromPageProps()
    void fetchSiteCartSnapshot($page.props as Record<string, unknown>)

    const offFinish = router.on('finish', () => {
      syncFromPageProps()
      void fetchSiteCartSnapshot($page.props as Record<string, unknown>)
    })

    return () => offFinish()
  })

  onDestroy(() => {
    document.body.classList.remove('site-ui')
    document.documentElement.style.backgroundColor = ''
  })
</script>

<svelte:head>
  <title>{title}</title>
  {#if description}
    <meta name="description" content={description} />
  {/if}
</svelte:head>

<div class="site-ui flex min-h-screen w-full max-w-full flex-col overflow-x-clip">
  <SiteFlash />
  <SiteHeader />
  <main
    class="min-w-0 w-full max-w-full flex-1 overflow-x-clip {cartHasItems
      ? 'pb-24 sm:pb-28'
      : ''}"
  >
    {@render children()}
  </main>
  <SiteFooter />
  {#if showSiteCommerce}
    <SiteCartDrawer />
    <SiteProductModal />
    <SiteFloatingCart />
  {/if}
</div>
